import type React from 'react';
import { useEffect } from 'react';

import { Box, ToggleButton } from '@zillow/constellation-10';

import type { Field, Section } from '../../../models/index';
import { PETS } from '../../../constants/index';
import { MAX_AMOUNT_ERROR_MESSAGE } from '../../../constants/stringConstants';
import PetsYesAndNoExpansion from './Pets/PetsYesAndNoExpansion';
import PetsInsuranceYesAndNo from './Pets/PetsInsuranceYesAndNo';
import PetsBaseRentButtonGroup from './Pets/PetsBaseRentButtonGroup';
import PetsCurrencyInput from './Pets/PetsCurrencyInput';

const BOUNDS = PETS.BOUNDS.V3;

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

const INITIAL_STATE = {
  [PETS.FIELD_NAME.IS_ENABLED]: false,
  [PETS.FIELD_NAME.REQUIRES_INSURANCE_COVERAGE]: null,
  [PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH]: '',
  [PETS.FIELD_NAME.REFUNDABLE_DEPOSIT]: '',
  [PETS.FIELD_NAME.IS_NO_ADDITIONAL_CHARGE]: null,
  [PETS.FIELD_NAME.IS_MONTHLY]: null,
  [PETS.FIELD_NAME.IS_DEPOSIT_REFUNDABLE]: null,
};

function Pets_v3({ section, setSectionValues }: Props): JSX.Element {
  useEffect(() => {
    section.setCustomFieldValidator(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT, isDepositAmountValid);
  }, []);

  function isDepositAmountValid(amount: string, field?: Field): boolean {
    if (Number(amount.replace(/,/g, '')) > BOUNDS.MAX_DEPOSIT_AMOUNT) {
      (field ?? section.getField(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT)).setErrorMessage(
        MAX_AMOUNT_ERROR_MESSAGE(BOUNDS.MAX_DEPOSIT_AMOUNT),
      );
      return false;
    }
    return true;
  }

  function onChargesChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;
    const { fieldName } = event.currentTarget.dataset;

    setSectionValues({ [fieldName]: value });
  }

  function onNoPetsClick(): void {
    setSectionValues(INITIAL_STATE);
  }

  function onYesPetsClick(): void {
    setSectionValues({ [PETS.FIELD_NAME.IS_ENABLED]: true });
  }

  function onNoInsuranceClick(): void {
    setSectionValues({ [PETS.FIELD_NAME.REQUIRES_INSURANCE_COVERAGE]: false });
  }

  function onYesInsuranceClick(): void {
    setSectionValues({ [PETS.FIELD_NAME.REQUIRES_INSURANCE_COVERAGE]: true });
  }

  function onNoAdditionalFeeClick(): void {
    setSectionValues({
      [PETS.FIELD_NAME.IS_NO_ADDITIONAL_CHARGE]: true,
      [PETS.FIELD_NAME.IS_MONTHLY]: false,
      [PETS.FIELD_NAME.IS_DEPOSIT_REFUNDABLE]: false,
      [PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH]: '',
      [PETS.FIELD_NAME.REFUNDABLE_DEPOSIT]: '',
    });
  }

  function onMonthlyClick(): void {
    setSectionValues({
      [PETS.FIELD_NAME.IS_NO_ADDITIONAL_CHARGE]: false,
      [PETS.FIELD_NAME.IS_MONTHLY]: true,
      [PETS.FIELD_NAME.IS_DEPOSIT_REFUNDABLE]: false,
      [PETS.FIELD_NAME.REFUNDABLE_DEPOSIT]: '',
    });
  }

  function onRefundableDepositClick(): void {
    setSectionValues({
      [PETS.FIELD_NAME.IS_NO_ADDITIONAL_CHARGE]: false,
      [PETS.FIELD_NAME.IS_MONTHLY]: false,
      [PETS.FIELD_NAME.IS_DEPOSIT_REFUNDABLE]: true,
      [PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH]: '',
    });
  }

  const isNoAdditionalCharge = section.getFieldValue(PETS.FIELD_NAME.IS_NO_ADDITIONAL_CHARGE);
  const isMonthly = section.getFieldValue(PETS.FIELD_NAME.IS_MONTHLY);
  const isRefundable = section.getFieldValue(PETS.FIELD_NAME.IS_DEPOSIT_REFUNDABLE);

  return (
    <PetsYesAndNoExpansion
      yesHandler={onYesPetsClick}
      noHandler={onNoPetsClick}
      yesActive={section.getFieldValue(PETS.FIELD_NAME.IS_ENABLED)}
    >
      <Box css={{ marginTop: 'layout.tight', marginBottom: 'layout.tight' }}>
        <PetsInsuranceYesAndNo
          dataTest={PETS.FIELD_NAME.REQUIRES_INSURANCE_COVERAGE}
          noHandler={onNoInsuranceClick}
          yesActive={section.getFieldValue(PETS.FIELD_NAME.REQUIRES_INSURANCE_COVERAGE)}
          yesHandler={onYesInsuranceClick}
        />
      </Box>
      <PetsBaseRentButtonGroup data-test="pet-charge-button-group">
        <ToggleButton
          selected={isNoAdditionalCharge}
          data-test="no-additional-charges-button"
          onClick={onNoAdditionalFeeClick}
        >
          No additional charge
        </ToggleButton>
        <ToggleButton selected={isMonthly} data-test="monthly-pet-rent-button" onClick={onMonthlyClick}>
          Yes, monthly pet rent
        </ToggleButton>
        <ToggleButton selected={isRefundable} data-test="refundable-deposit-button" onClick={onRefundableDepositClick}>
          Yes, refundable deposit
        </ToggleButton>
      </PetsBaseRentButtonGroup>
      <Box css={{ marginBottom: 'layout.tight' }} />
      {isMonthly && (
        <PetsCurrencyInput
          label="Pet rent"
          value={section.getFieldValue(PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH)}
          onChange={onChargesChange}
          onBlur={onChargesChange}
          field={section.getField(PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH)}
          dataFieldName={PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH}
          perMonth
          formHelp="Amount generally should not exceed $75 per pet"
        />
      )}
      {isRefundable && (
        <PetsCurrencyInput
          label="Refundable pet deposit"
          value={section.getFieldValue(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT)}
          onChange={onChargesChange}
          onBlur={onChargesChange}
          field={section.getField(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT)}
          dataFieldName={PETS.FIELD_NAME.REFUNDABLE_DEPOSIT}
          perMonth={false}
          formHelp="A refundable pet deposit is treated as a security deposit"
        />
      )}
    </PetsYesAndNoExpansion>
  );
}

Pets_v3.description = `Pets screen with bounds of ${JSON.stringify(BOUNDS)}`;

export default Pets_v3;
